import type * as React from 'react'
import { useCallback, useMemo } from 'react'
import type { JSX } from 'react'

import { Slot } from '@buffer-mono/popcorn'

import { useCurrentOrganization } from '~publish/contexts/AccountContext'
import { usePostComposer } from '~publish/hooks/usePostComposer'
import type { CreateNewPostInComposerData } from '~publish/hooks/usePostComposer'

type NewPostComposerTriggerProps = CreateNewPostInComposerData & {
  children: React.ReactNode
  className?: string
}

/**
 * Use this component to trigger opening the composer for a new post.
 *
 * @example
 * ```tsx
 * <NewPostComposerTrigger prefillPostData={{text: "Hello"}}>
 *  <Button>Open</Button>
 * </NewPostComposerTrigger>
 * ````
 */
const NewPostComposerTrigger = ({
  children,
  className,
  ...openComposerArgs
}: NewPostComposerTriggerProps): JSX.Element | null => {
  const currentOrganization = useCurrentOrganization()
  const { triggerAttributes, createNewPostInComposer } = usePostComposer()

  /**
   * This is used to preselect channels when the user is creating a new post.
   * There are many scenarios/actions a user can take to decide what channels should be preselected in the composer,
   * so we rely on the following logic to determine the preselected channels.
   *
   * 1. Preselect the provided channels when set – consumer who use this component can pass in their predefined channels and it will be used by default.
   *    This is used in:
   *    - The All Channels page with channel filtering set in url params
   *    - The SingleChannel pages
   *
   * 2. If no channelIds are provided:
   *    Preselect all channels when the user has 3 or less channels connected
   *    Preselect no channels when the user has more than 3 channels connected
   */
  const preselectedChannelIds = useMemo(() => {
    if (openComposerArgs.channels?.length) {
      return openComposerArgs.channels
    }

    const unlockedChannelIds = currentOrganization?.channels?.length
      ? currentOrganization.channels
          .filter((channel) => !channel.isLocked)
          .map((channel) => channel.id)
      : []

    return unlockedChannelIds.length <= 3 ? unlockedChannelIds : []
  }, [currentOrganization, openComposerArgs.channels])

  const handleOpen = useCallback(
    (evt: React.MouseEvent<HTMLElement>) => {
      evt.preventDefault()

      void createNewPostInComposer({
        ...openComposerArgs,
        channels: preselectedChannelIds,
      })
    },
    [createNewPostInComposer, openComposerArgs, preselectedChannelIds],
  )

  return (
    <Slot.Root
      {...triggerAttributes}
      onClick={handleOpen}
      className={className}
    >
      {children}
    </Slot.Root>
  )
}

export { NewPostComposerTrigger }
export type { NewPostComposerTriggerProps }
